import { messagingApi, candidateApi, matchesApi, signupApi, normalizeMatchScore } from '../../shared/utils/apiClient';

// Types for job recommendations
export interface JobRecommendation {
    job_id: string;
    title: string;
    company_name: string;
    location: string;
    employment_type: string;
    match_score: number;
    match_reasons?: string[];
}

export interface CandidateForRecommendation {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    yearsOfExperience?: number;
    currentTitle?: string;
    location?: string;
    skills?: string[];
    lastContactedAt?: string;
    lastMessage?: {
        content: string;
        senderId: string;
        timestamp: string;
    };
    // Imported candidate-specific fields
    linkedinUrl?: string;
    importSource?: string;
    reviewStatus?: string;
    source?: 'platform' | 'imported' | 'imported_se';
}

export interface ImportedCandidatesResponse {
    candidates: ImportedCandidateRaw[];
    total: number;
    page: number;
    limit: number;
}

interface ImportedCandidateRaw {
    id: string;
    email: string;
    name: string;
    skills?: string[];
    yearsExp?: number;
    allJobTitles?: string[];
    allCompanies?: string[];
    locations?: string[];
    degreesList?: string[];
    linkedinUrl?: string;
    importSource?: string;
    reviewStatus?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SendRecommendationEmailRequest {
    candidateEmail: string;
    candidateName: string;
    jobs: { title: string; companyName: string; location: string; matchScore: number }[];
    message: string;
}

export interface JobMatchesResponse {
    candidateId: string;
    candidateName: string;
    matches: JobRecommendation[];
    totalJobs: number;
}

export interface SendAsDavidRequest {
    target_user_id: string;
    content: string;
    job_recommendations: JobRecommendation[];
}

export interface SendAsDavidResponse {
    conversation_id: string;
    message_id: string;
    created_at: string;
}

export interface DavidConversation {
    id: string;
    candidateId: string;
    candidateName?: string;
    candidateEmail?: string;
    lastMessage?: {
        content: string;
        senderId: string;
        timestamp: string;
    };
    createdAt: string;
    updatedAt: string;
}

export interface DavidMessageResponse {
    id: string;
    conversation_id: string;
    sender_type: 'david_bot' | 'candidate';
    sender_name: string;
    content: string;
    message_type: 'text' | 'job_recommendation';
    job_recommendations?: JobRecommendation[];
    created_at: string;
}

// ── Snake_case → camelCase mappers ──────────────────────────────────────────

function mapConversation(c: any): DavidConversation {
    return {
        id: c.id,
        candidateId: c.candidate_id,
        candidateName: c.candidate_name,
        candidateEmail: c.candidate_email,
        lastMessage: c.last_message ? {
            content: c.last_message.content,
            senderId: c.last_message.sender_id,
            timestamp: c.last_message.timestamp
        } : undefined,
        createdAt: c.created_at,
        updatedAt: c.updated_at
    };
}

function mapJobMatch(match: any): JobRecommendation {
    const job = match.job || {};
    let jobId = job.id || job._id || '';
    if (typeof jobId === 'object' && jobId.$oid) jobId = jobId.$oid;
    else if (typeof jobId === 'object') jobId = String(jobId);

    return {
        job_id: jobId,
        title: job.title || 'Untitled Position',
        company_name: job.companyName || job.company_name || 'Unknown Company',
        location: job.location || 'Remote',
        employment_type: job.employmentType || job.employment_type || 'Full-time',
        match_score: normalizeMatchScore(
            match.vectorSimilarity || 0,
            match.qwenScore || 0,
            match.softCriteriaScore || 0
        ),
        match_reasons: match.matchReasons || []
    };
}

// ── API Functions ───────────────────────────────────────────────────────────

/**
 * Fetch all candidates from m_candidates collection for the recommendation list
 */
export const fetchApprovedCandidates = async (): Promise<CandidateForRecommendation[]> => {
    const candidates = await candidateApi.get<any[]>(`/candidate/v1/list?page=1&size=1000`);

    return (candidates || []).map((cand: any) => ({
        id: cand.id || cand._id,
        email: cand.email,
        firstName: cand.firstName || cand.first_name || '',
        lastName: cand.lastName || cand.last_name || '',
        phone: cand.phone,
        yearsOfExperience: cand.parsedResume?.totalYearsExp || cand.parsed_resume?.total_years_exp,
        currentTitle: cand.parsedResume?.experience?.[0]?.title || cand.parsed_resume?.experience?.[0]?.title,
        location: cand.location,
        skills: cand.parsedResume?.skills || cand.parsed_resume?.skills || [],
        lastContactedAt: cand.lastContactedAt || cand.last_contacted_at
    }));
};

/**
 * Get job recommendations for a specific candidate
 */
export const getJobRecommendations = async (candidateId: string): Promise<JobMatchesResponse> => {
    const data = await matchesApi.post<any>(
        `/admin/v1/candidates/${candidateId}/find-jobs`,
        {}
    );

    return {
        candidateId: data.candidateId || candidateId,
        candidateName: data.candidateName || '',
        matches: (data.matches || []).map(mapJobMatch),
        totalJobs: data.totalJobs || (data.matches || []).length
    };
};

/**
 * Send a message as David to a candidate with job recommendations
 */
export const sendAsDavid = async (request: SendAsDavidRequest): Promise<SendAsDavidResponse> => {
    return messagingApi.post<SendAsDavidResponse>('/admin/send-as-david', request);
};

/**
 * Get all David conversations (for recently contacted candidates view)
 */
export const getDavidConversations = async (): Promise<DavidConversation[]> => {
    const { conversations } = await messagingApi.get<{ conversations: any[] }>(
        '/admin/david-conversations'
    );
    return (conversations || []).map(mapConversation);
};

/**
 * Get messages in a specific David conversation
 */
export const getDavidMessages = async (conversationId: string): Promise<DavidMessageResponse[]> => {
    const { messages } = await messagingApi.get<{ messages: DavidMessageResponse[] }>(
        `/david/messages?conversation_id=${conversationId}`
    );
    return messages || [];
};

// ============================================================================
// IMPORTED CANDIDATES API
// ============================================================================

/** Which imported sub-collection to query. 'all_roles' is the default
 *  (m_imp_candidates_all_roles); 'software_engineering' reads from the
 *  m_imp_candidates_software_engineering collection. */
export type ImportedCollection = 'all_roles' | 'software_engineering';

/**
 * Fetch paginated imported candidates from matches-vectors service.
 * @param collection optional — which imported sub-collection to read from.
 */
export const fetchImportedCandidates = async (
    page: number = 1,
    limit: number = 20,
    search: string = '',
    collection: ImportedCollection = 'all_roles'
): Promise<{ candidates: CandidateForRecommendation[]; total: number; page: number; limit: number }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (collection && collection !== 'all_roles') params.set('collection', collection);

    const data = await matchesApi.get<ImportedCandidatesResponse>(
        `/admin/v1/imported-candidates?${params}`
    );

    // Tag each candidate with the right source so downstream code (modal,
    // tabs, email flow) can distinguish all_roles vs software_engineering.
    const sourceTag: 'imported' | 'imported_se' =
        collection === 'software_engineering' ? 'imported_se' : 'imported';

    const candidates: CandidateForRecommendation[] = data.candidates.map(c => {
        const nameParts = (c.name || '').trim().split(/\s+/);
        return {
            id: c.id,
            email: c.email,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            yearsOfExperience: c.yearsExp,
            currentTitle: c.allJobTitles?.[0],
            location: c.locations?.[0],
            skills: c.skills,
            linkedinUrl: c.linkedinUrl,
            importSource: c.importSource,
            reviewStatus: c.reviewStatus,
            source: sourceTag,
        };
    });

    return { candidates, total: data.total, page: data.page, limit: data.limit };
};

/**
 * Get job recommendations for an imported candidate.
 * @param collection — which imported sub-collection the candidate came from.
 */
export const getImportedCandidateJobRecommendations = async (
    candidateId: string,
    collection: ImportedCollection = 'all_roles'
): Promise<JobMatchesResponse> => {
    const qs = collection === 'software_engineering' ? '?collection=software_engineering' : '';
    const data = await matchesApi.post<any>(
        `/admin/v1/imported-candidates/${candidateId}/find-jobs${qs}`,
        {}
    );

    return {
        candidateId: data.candidateId || candidateId,
        candidateName: data.candidateName || '',
        matches: (data.matches || []).map(mapJobMatch),
        totalJobs: data.totalJobs || (data.matches || []).length
    };
};

/**
 * Send job recommendation email to an imported candidate
 */
export const sendRecommendationEmail = async (request: SendRecommendationEmailRequest): Promise<void> => {
    await signupApi.post('/admin/v1/send-recommendation-email', request);
};
